import React, { useState } from "react";
import { Table, Button, Tabs, Input, Card, Space, Empty, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { UnifiedContactModal } from "./UnifiedContactModal";

const { Search } = Input;

export interface UnifiedContactViewProps {
  title: string;
  roles: { key: string; label: string; icon?: React.ReactNode }[];
  activeRole: string;
  onRoleChange: (role: string) => void;
  dataSource: any[];
  loading: boolean;
  onSearch: (text: string) => void;
  onSave: (values: any) => Promise<void>;
  onSuccess?: (data?: any) => void;
  validationMode: 'strict' | 'relaxed';
  columns: any[];
  pagination?: any;
  onRowClick?: (record: any) => void;
  onEditRequest?: (record: any) => Promise<any> | any;
}

export const UnifiedContactView: React.FC<UnifiedContactViewProps> = ({
  title,
  roles,
  activeRole,
  onRoleChange,
  dataSource,
  loading,
  onSearch,
  onSave,
  onSuccess,
  validationMode,
  columns,
  pagination,
  onRowClick,
  onEditRequest
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [editingContact, setEditingContact] = useState<any>(undefined);
  const [localLoading, setLocalLoading] = useState(false);

  const handleCreate = () => {
    setEditingContact(undefined);
    setModalVisible(true);
  };

  const handleEdit = async (record: any) => {
    if (onEditRequest) {
      setEditingContact(record);
      setModalVisible(true);
      setLocalLoading(true);
      try {
        const fullRecord = await onEditRequest(record);
        setEditingContact(fullRecord && Object.keys(fullRecord).length > 0 ? fullRecord : record);
      } catch (error: any) {
        console.error("Failed to fetch full record:", error);
        message.error(error?.message || "Error al obtener los datos del contacto");
        setModalVisible(false);
        setEditingContact(undefined);
      } finally {
        setLocalLoading(false);
      }
    } else {
      setEditingContact(record);
      setModalVisible(true);
    }
  };

  const handleSaveInternal = async (values: any) => {
    setLocalLoading(true);
    try {
      await onSave(values);
      setModalVisible(false);
      if (onSuccess) onSuccess(values);
    } finally {
      setLocalLoading(false);
    }
  };

  const renderTable = (roleKey: string) => (
    <Table
      dataSource={dataSource}
      columns={columns}
      loading={loading}
      rowKey="uid"
      pagination={pagination}
      onRow={(record) => ({
        onClick: () => onRowClick ? onRowClick(record) : handleEdit(record),
        style: { cursor: 'pointer' }
      })}
      locale={{ emptyText: <Empty description="No se encontraron contactos" /> }}
    />
  );

  return (
    <div className="container-fluid">
      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
            <span style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{title}</span>
            <Space>
              <Search
                placeholder="Buscar contacto..."
                allowClear
                onSearch={onSearch}
                style={{ width: 250 }}
                enterButton
              />
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                Nuevo Contacto
              </Button>
            </Space>
          </div>
        }
        className="shadow-sm"
      >
        {roles.length > 1 ? (
          <Tabs
            activeKey={activeRole}
            onChange={onRoleChange}
            items={roles.map(role => ({
              key: role.key,
              label: (
                <span>
                  {role.icon}
                  {role.label}
                </span>
              ),
              children: renderTable(role.key)
            }))}
          />
        ) : (
          renderTable(activeRole)
        )}
      </Card>

      <UnifiedContactModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onSuccess={() => {}} 
        initialValues={editingContact}
        validationMode={validationMode}
        role={activeRole as any}
        onSave={handleSaveInternal}
        loading={localLoading}
      />
    </div>
  );
};
